import { QueryClient, type DefaultOptions, type QueryKey } from '@tanstack/react-query';
import { ApiError } from './client';

/**
 * How long a fetched result is served from the cache without a request. A route
 * change inside this window renders instantly from cache; after it, the cached
 * data still renders while a background refetch brings it up to date.
 */
export const STALE_TIME_MS = 30_000;

/** Options a page may pass to a query hook. `enabled: false` keeps the query idle. */
export interface QueryOptions {
  enabled?: boolean;
}

/**
 * One retry, and only for a network error or a 5xx. A 4xx problem (validation,
 * 401, 403, 404, 429) is the API's answer and must surface at once, never be
 * retried against its rate limit.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  return !(error instanceof ApiError && error.status < 500);
}

/**
 * The app's single server-state cache. main.tsx builds one for the app; tests
 * build their own per render with `retry: false`.
 */
export function createQueryClient(queries: DefaultOptions['queries'] = {}): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_TIME_MS,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: shouldRetry,
        ...queries,
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Marks every query under the given key prefixes stale and refetches the mounted
 * ones. Returned from a mutation's onSuccess so `isPending` lasts until the lists
 * on screen are fresh again.
 */
export async function invalidate(
  queryClient: QueryClient,
  ...queryKeys: QueryKey[]
): Promise<void> {
  await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}
